import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountryDto } from '../../../services/api/dictionary';

/**
 * Выпадающий список стран с поиском по названию/коду — замена обычному <select>,
 * который неудобно листать при большом справочнике. Работает как обычный
 * ReactiveForms-контрол (formControlName="..."), значение — CountryDto.id.
 */
@Component({
  selector: 'app-country-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './country-select.html',
  styleUrl: './country-select.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CountrySelectComponent),
    multi: true
  }]
})
export class CountrySelectComponent implements ControlValueAccessor, OnChanges {
  @Input() countries: CountryDto[] = [];
  @Input() placeholder = 'Не указана';
  @Input() searchPlaceholder = 'Поиск страны...';

  searchText = '';
  isOpen = false;
  disabled = false;
  filteredCountries: CountryDto[] = [];

  private selectedId: number | null = null;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countries']) {
      this.filteredCountries = this.countries;
      this.syncSearchTextFromSelection();
    }
  }

  writeValue(value: number | null): void {
    this.selectedId = value ?? null;
    this.syncSearchTextFromSelection();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get selectedCountry(): CountryDto | undefined {
    return this.countries.find(c => c.id === this.selectedId);
  }

  getCountryName(country: CountryDto): string {
    return country.name || country.nameRu;
  }

  private syncSearchTextFromSelection(): void {
    if (this.isOpen) return;
    const c = this.selectedCountry;
    this.searchText = c ? this.getCountryName(c) : '';
  }

  onFocus(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.searchText = '';
    this.filteredCountries = this.countries;
  }

  onInput(value: string): void {
    this.searchText = value;
    const q = value.trim().toLowerCase();
    this.filteredCountries = !q
      ? this.countries
      : this.countries.filter(c =>
          this.getCountryName(c).toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q));
  }

  selectCountry(country: CountryDto): void {
    this.selectedId = country.id;
    this.searchText = this.getCountryName(country);
    this.isOpen = false;
    this.onChange(this.selectedId);
    this.onTouched();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectedId = null;
    this.searchText = '';
    this.onChange(null);
    this.onTouched();
  }

  /** Задержка нужна, чтобы клик (mousedown) по варианту в списке успел сработать
   *  раньше, чем скроется список по blur — как в остальных автокомплитах проекта. */
  onBlur(): void {
    setTimeout(() => {
      this.isOpen = false;
      this.syncSearchTextFromSelection();
      this.onTouched();
    }, 200);
  }
}
